/* eslint-disable jsx-a11y/control-has-associated-label */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import logger from 'src/util/logger';

import TradeRuleListTableItem from './TradeRuleListTableItem';

/**
 * Minimal table skeleton rendered behind the pagination error modal. Keeps the
 * error surface local to the list so the route-level PlaceholderErrorBoundary is
 * never triggered and the already-loaded rows stay mounted above it.
 * @returns {React.ReactElement} Placeholder skeleton.
 */
function TradeRuleListTablePaginationPlaceholder() {
  return (
    <div className="mt-4 mb-4 card" aria-hidden="true">
      <div className="table-responsive">
        <table className="table">
          <tbody>
            {[0, 1, 2].map((rowIdx) => (
              <tr key={`trade-rule-pagination-placeholder-row-${rowIdx}`}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((cellIdx) => (
                  <td key={`trade-rule-pagination-placeholder-row-${rowIdx}-cell-${cellIdx}`}>
                    <span className="placeholder placeholder-glow bg-darken col-10" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TradeRuleListTableFragment = graphql`
  fragment TradeRuleListTable_meter on Meter 
  @throwOnFieldError
  @refetchable(queryName: "TradeRuleListTable_Pagination_Query") 
  @argumentDefinitions(
    first: { type: "Int"}
    after: { type: "String" }
    last: { type: "Int"}
    before: { type: "String" }
  ) {
    uuid
    identifier
    title
    tradePointId
    active {
      start
      finish
    }
    rules(
      after: $after
      before: $before
      first: $first
      last: $last
    ) @connection(key: "TradeRuleListTable_rules") {
      edges {
        cursor
        node {
          uuid
          ...TradeRuleListTableItem_rule
        }
      }
    }
  }
`;

/**
 * TradeRuleListTable functional component displays a paginated table of trade rules for a meter.
 * @param {object} props - Component props.
 * @param {object} props.meter - Meter fragment reference.
 * @param {string} props.propertyId - The property ID for navigation links.
 * @returns {React.ReactElement} TradeRuleListTable component.
 */
function TradeRuleListTable({ meter: meterRef, propertyId }) {
  const intl = useIntl();

  const {
    data: meter, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(TradeRuleListTableFragment, meterRef);

  const [paginationError, setPaginationError] = useState(null);

  if (!meter) {
    return <Loading />;
  }

  const trades = (!meter.rules || !meter.rules.edges)
    ? [] : meter.rules.edges.map((el) => (el.node));

  const clauseHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list.trade_rule_list_table.header.clauses.label', defaultMessage: 'Clauses' });

  const copyLoadMore = intl.formatMessage({ id: 'trade_rule_list.trade_rule_list_table.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'trade_rule_list_table.load_more_button.state_no_more.label', defaultMessage: 'No more trade rules to load' });
  const copyPaginationErrorModalBodyText = intl.formatMessage({ id: 'trade_rule.trade_rule_list.trade_rule_list_table.pagination_error.message', defaultMessage: 'Failed to load more trade rules. Please try again.' });

  const idHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.id.label', defaultMessage: 'ID' });
  const typeHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.type.label', defaultMessage: 'Type' });
  const stateHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.state.label', defaultMessage: 'State' });
  const directionHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.direction.label', defaultMessage: 'Direction' });
  const counterpartyTraderHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.counterparty_trader.label', defaultMessage: 'Counterparty Trader' });
  const counterpartyTradePointHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.counterparty_trade_point.label', defaultMessage: 'Counterparty Trade Point' });
  const validityHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.head.validity.label', defaultMessage: 'Validity' });

  /**
   * Dismisses the pagination error modal by clearing the captured error.
   * @returns {void}
   */
  const handlePaginationErrorDismiss = () => setPaginationError(null);

  /**
   * Loads the next page of trade rules for the meter. Pagination errors (network
   * and field errors) are delivered via the loadNext onComplete callback and
   * captured in local state so they surface in an inline modal rather than the
   * route-level error boundary.
   * @returns {void}
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(
      50,
      {
        onComplete: (error) => {
          if (error) {
            logger.error({ error });
            setPaginationError(error);
          }
        },
      },
    );
  };

  return (
    <div className="mt-4 mb-4 card">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{idHeaderLabel}</th>
              <th scope="col">{typeHeaderLabel}</th>
              <th scope="col">{stateHeaderLabel}</th>
              <th scope="col">{directionHeaderLabel}</th>
              <th scope="col">{counterpartyTraderHeaderLabel}</th>
              <th scope="col">{counterpartyTradePointHeaderLabel}</th>
              <th scope="col">{clauseHeaderLabel}</th>
              <th scope="col">{validityHeaderLabel}</th>
            </tr>
          </thead>
          <tbody>
            {trades
                && trades.map((tradeRule) => (
                  <TradeRuleListTableItem
                    tradePointId={meter.tradePointId}
                    rule={tradeRule}
                    propertyId={propertyId}
                    key={tradeRule.uuid}
                  />
                ))}
          </tbody>
        </table>
      </div>
      {paginationError && (
      <PlaceholderErrorModal
        bodyText={copyPaginationErrorModalBodyText}
        error={paginationError}
        onCancelFunc={handlePaginationErrorDismiss}
        onRetryFunc={loadMore}
        placeholderComponent={TradeRuleListTablePaginationPlaceholder}
        retryDisabled={isLoadingNext}
      />
      )}
      <div className="card-footer">
        <LoadMoreButton
          copyLoadMore={copyLoadMore}
          copyNoMore={copyNoMore}
          hasNext={hasNext}
          onClick={loadMore}
        />
      </div>
    </div>
  );
}

TradeRuleListTable.propTypes = {
  meter: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  propertyId: PropTypes.string.isRequired,
};

export default TradeRuleListTable;
